/**
 * Reads TikTok ad-click attribution identifiers from the browser at signup time
 * — the TikTok counterpart to {@link getMetaClickIds} / {@link getGoogleClickIds}.
 *
 * When a user clicks a TikTok ad, TikTok appends `ttclid` to the landing URL and
 * the TikTok Pixel stores a browser id in the `_ttp` cookie. Those are set on the
 * registrable domain (`.emergecareer.com`), so they are readable across
 * subdomains including the app.
 *
 * We persist these on the user doc so the server-side Events API conversion
 * (fired at signup and later milestones, potentially days later) can send
 * `ttclid`/`ttp` for deterministic ad-click attribution — no browser needed at
 * that point. This is the direct analog of Meta's `fbc`/`fbp`.
 *
 * `ttclid`/`ttp` are TikTok's opaque click/browser ids; they are sent RAW (never
 * hashed). Only keys that resolve to a value are included.
 */
export interface TiktokClickIds {
  ttclid?: string;
  ttp?: string;
}

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined" || !document.cookie) return undefined;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  if (!match) return undefined;
  const value = decodeURIComponent(match.slice(name.length + 1));
  return value || undefined;
}

export function getTiktokClickIds(): TiktokClickIds {
  const ttclid =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("ttclid") || undefined
      : undefined;
  const ttp = readCookie("_ttp");

  const ids: TiktokClickIds = {};
  if (ttclid) ids.ttclid = ttclid;
  if (ttp) ids.ttp = ttp;
  return ids;
}
